import React, { useState } from 'react';
import {
    Package, Edit2, Trash2, MoreHorizontal, AlertTriangle, Plus, Minus,
    Tag, TrendingUp, TrendingDown, Eye
} from 'lucide-react';
import ConfirmDeleteModal from './ConfirmDeleteModal';

const ProductCard = ({
    product,
    viewMode = 'grid',
    onEdit,
    onDelete,
    onAdjustStock,
    onViewDetails,
    categoryName,
}) => {
    const [showMenu, setShowMenu] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const getStockStatus = () => {
        const qty = product.quantity || 0;
        const minLevel = product.min_stock_level || 5;

        if (qty === 0) return { status: 'out', label: 'Out of Stock', color: 'red' };
        if (qty <= minLevel) return { status: 'low', label: 'Low Stock', color: 'amber' };
        return { status: 'ok', label: 'In Stock', color: 'emerald' };
    };

    const stockStatus = getStockStatus();

    const formatPrice = (price) => {
        if (!price) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(price);
    };

    const getProfit = () => {
        if (!product.cost_price || !product.selling_price) return null;
        return product.selling_price - product.cost_price;
    };

    const getProfitMargin = () => {
        const profit = getProfit();
        if (profit === null) return null;
        return ((profit / product.selling_price) * 100).toFixed(1);
    };

    const profit = getProfit();
    const profitMargin = getProfitMargin();

    const deleteModal = (
        <ConfirmDeleteModal
            isOpen={showDeleteConfirm}
            onConfirm={() => {
                setShowDeleteConfirm(false);
                onDelete?.();
            }}
            onCancel={() => setShowDeleteConfirm(false)}
            title="Delete Product"
            message={`Are you sure you want to delete "${product.name}"? This action cannot be undone.`}
        />
    );

    // Grid view
    if (viewMode === 'grid') {
        return (
            <>
                {deleteModal}
                <div className="group bg-white/[0.02] border border-white/10 rounded-xl overflow-hidden hover:border-white/20 transition-all hover:shadow-lg">
                    {/* Image */}
                    <div className="relative h-40 bg-white/5">
                        {product.images && product.images.length > 0 ? (
                            <img
                                src={product.images[0]}
                                alt={product.name}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <Package size={40} className="text-white/20" />
                            </div>
                        )}

                        {/* Stock Badge */}
                        <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-semibold ${
                            stockStatus.color === 'red' ? 'bg-red-500/20 text-red-500' :
                            stockStatus.color === 'amber' ? 'bg-amber-500/20 text-amber-500' :
                            'bg-emerald-500/20 text-emerald-500'
                        }`}>
                            {stockStatus.label}
                        </div>

                        {/* Quick Actions */}
                        <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAdjustStock?.('add');
                                }}
                                className="p-1.5 bg-emerald-500/90 text-white rounded-lg hover:bg-emerald-500 transition"
                                title="Add Stock"
                            >
                                <Plus size={14} />
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAdjustStock?.('subtract');
                                }}
                                className="p-1.5 bg-red-500/90 text-white rounded-lg hover:bg-red-500 transition"
                                title="Remove Stock"
                            >
                                <Minus size={14} />
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-white truncate">{product.name}</h3>
                                <p className="text-xs text-white/40 mt-0.5">{product.sku}</p>
                            </div>
                            <div className="relative">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowMenu(!showMenu);
                                    }}
                                    className="p-1 text-white/40 hover:text-white rounded hover:bg-white/5 transition"
                                >
                                    <MoreHorizontal size={16} />
                                </button>
                                {showMenu && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-10"
                                            onClick={() => setShowMenu(false)}
                                        />
                                        <div className="absolute right-0 top-full mt-1 w-36 bg-[#0d1424] border border-white/10 rounded-lg shadow-xl z-20 overflow-hidden">
                                            <button
                                                onClick={() => {
                                                    setShowMenu(false);
                                                    onViewDetails?.();
                                                }}
                                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:bg-white/5 transition"
                                            >
                                                <Eye size={14} />
                                                View Details
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowMenu(false);
                                                    onEdit?.();
                                                }}
                                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:bg-white/5 transition"
                                            >
                                                <Edit2 size={14} />
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowMenu(false);
                                                    setShowDeleteConfirm(true);
                                                }}
                                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-500/10 transition"
                                            >
                                                <Trash2 size={14} />
                                                Delete
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {categoryName && (
                            <div className="flex items-center gap-1 mt-2">
                                <Tag size={12} className="text-[#ff4d4d]" />
                                <span className="text-xs text-white/50">{categoryName}</span>
                            </div>
                        )}

                        <div className="flex items-end justify-between mt-3">
                            <div>
                                <p className="text-lg font-bold text-white">{formatPrice(product.selling_price)}</p>
                                {profitMargin !== null && (
                                    <div className={`flex items-center gap-1 text-xs ${
                                        profit > 0 ? 'text-emerald-500' : 'text-red-500'
                                    }`}>
                                        {profit > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                        {profitMargin}% margin
                                    </div>
                                )}
                            </div>
                            <div className="text-right">
                                <p className={`text-lg font-bold ${
                                    stockStatus.color === 'red' ? 'text-red-500' :
                                    stockStatus.color === 'amber' ? 'text-amber-500' :
                                    'text-white'
                                }`}>
                                    {product.quantity || 0}
                                </p>
                                <p className="text-xs text-white/40">{product.unit || 'pcs'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // List view
    return (
        <>
            {deleteModal}
            <div className="group bg-white/[0.02] border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all flex items-center gap-4">
                {/* Image */}
                <div className="w-16 h-16 flex-shrink-0 bg-white/5 rounded-lg overflow-hidden">
                    {product.images && product.images.length > 0 ? (
                        <img
                            src={product.images[0]}
                            alt={product.name}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <Package size={24} className="text-white/20" />
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-white truncate">{product.name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            stockStatus.color === 'red' ? 'bg-red-500/20 text-red-500' :
                            stockStatus.color === 'amber' ? 'bg-amber-500/20 text-amber-500' :
                            'bg-emerald-500/20 text-emerald-500'
                        }`}>
                            {stockStatus.label}
                        </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-white/50">
                        <span>{product.sku}</span>
                        {categoryName && (
                            <span className="flex items-center gap-1">
                                <Tag size={12} />
                                {categoryName}
                            </span>
                        )}
                    </div>
                </div>

                {/* Stock */}
                <div className="text-center px-4">
                    <p className={`text-lg font-bold ${
                        stockStatus.color === 'red' ? 'text-red-500' :
                        stockStatus.color === 'amber' ? 'text-amber-500' :
                        'text-white'
                    }`}>
                        {product.quantity || 0}
                    </p>
                    <p className="text-xs text-white/40">{product.unit || 'pcs'}</p>
                </div>

                {/* Price */}
                <div className="text-right px-4">
                    <p className="text-lg font-bold text-white">{formatPrice(product.selling_price)}</p>
                    {product.cost_price && (
                        <p className="text-xs text-white/40">Cost: {formatPrice(product.cost_price)}</p>
                    )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onAdjustStock?.('add')}
                        className="p-2 text-white/40 hover:text-emerald-500 hover:bg-emerald-500/10 rounded-lg transition"
                        title="Add Stock"
                    >
                        <Plus size={18} />
                    </button>
                    <button
                        onClick={() => onAdjustStock?.('subtract')}
                        className="p-2 text-white/40 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition"
                        title="Remove Stock"
                    >
                        <Minus size={18} />
                    </button>
                    <button
                        onClick={onEdit}
                        className="p-2 text-white/40 hover:text-sky-400 hover:bg-sky-400/10 rounded-lg transition"
                        title="Edit"
                    >
                        <Edit2 size={18} />
                    </button>
                    <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="p-2 text-white/40 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition"
                        title="Delete"
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            </div>
        </>
    );
};

export default ProductCard;
